import React, {
  useState,
  useCallback,
  useRef,
  useMemo,
  useEffect,
} from "react";
import { Grid } from "@mui/material";
import { ProjectDetailPageProps } from "../../../types";
import { useEditMode } from "../../layout/ProjectLayout";
import { useToast } from "../../../contexts/ToastContext";
import { useAutoSave } from "../../../hooks/useAutoSave";
import { MapSection, useStandortSpeichern } from "../../map";
import { apiService } from "../../../services/api";
import ParcelsSection from "./sections/ParcelsSection";
import OwnersSection from "./sections/OwnersSection";
import AreaInfoSection from "./sections/AreaInfoSection";
import { formatNumber } from "../../../utils/formatters";
import { colors } from "../../../styles/theme";
import StatChip from "../../common/display/StatChip";
import { useAreaData } from "./hooks/useAreaData";
import { useParcelsData } from "./hooks/useParcelsData";
import { useOwnersData } from "./hooks/useOwnersData";
import { useStatusFilter } from "./hooks/useStatusFilter";
import { useGenerationUnitsData } from "../projectGenerationUnits/hooks/useGenerationUnitsData";
import ConfirmDialog from "../../common/feedback/ConfirmDialog";
import PageTemplate from "../../layout/PageTemplate";

interface AreaPageProps extends ProjectDetailPageProps {
  canView: boolean;
  canEdit: boolean;
}

type SelectionContext = "parcel" | "owner" | null;

// Muss exakt den Werten von FlaechenakquiseStatus (backend/core/enums.py) entsprechen.
const FLAECHENAKQUISE_NICHT_GESTARTET = "Nicht gestartet";
const FLAECHENAKQUISE_LAEUFT = "Flächensicherung läuft";
const FLAECHENAKQUISE_ABGESCHLOSSEN = "Flächen gesichert";

type StatusPopup = "gestartet" | "abgeschlossen" | null;

const AreaPage: React.FC<AreaPageProps> = ({ project, canEdit }) => {
  const { isEditMode } = useEditMode();
  const { showSuccess, showError } = useToast();

  const mapSectionRef = useRef<HTMLDivElement>(null);

  // Popup für automatische Status-Vorschläge (Flächenakquise)
  const [statusPopup, setStatusPopup] = useState<StatusPopup>(null);

  // Anlagen (nur für die Karte — die Liste liegt auf der Anlagen-Seite)
  const {
    units,
    reload: reloadUnits,
    updateUnit,
  } = useGenerationUnitsData({ projectId: project.id });

  // Owner-Auswahl State
  const [selectedOwnerId, setSelectedOwnerId] = useState<string | null>(null);

  // Area-Daten Hook
  const {
    areaData,
    isLoadingArea,
    loadError,
    flaechenbeschreibung,
    setFlaechenbeschreibung,
    loadAreaData,
  } = useAreaData({
    projectId: project.id,
    canEdit,
    isEditMode,
  });

  // Parcels-Daten Hook
  const {
    parcels,
    allParcels,
    isLoadingParcels,
    selectedParcelId,
    selectedParcel,
    parcelNotizen,
    setParcelNotizen,
    loadParcels,
    loadAllParcels,
    loadOwnerParcels,
    handleParcelClick: handleParcelClickInternal,
  } = useParcelsData({
    projectId: project.id,
    areaId: areaData?.id,
    canEdit,
    isEditMode,
    selectedOwnerId,
  });

  // Owners-Daten Hook
  const {
    filteredOwners,
    allOwners,
    isLoadingOwners,
    loadOwners,
    selectedOwner,
    ownerNotizen,
    setOwnerNotizen,
    updateOwnerStatus,
    updateOwnerNotes,
  } = useOwnersData({
    projectId: project.id,
    areaId: areaData?.id,
    selectedParcelId,
    selectedOwnerId,
    loadParcels,
    loadAllParcels,
    loadOwnerParcels,
  });

  const handleKoordinateChange = useStandortSpeichern(project.id);

  /** Nach Geometrie- oder ALKIS-Upload alle relevanten Daten neu laden */
  const handleGeometrieChanged = useCallback(async () => {
    await loadAreaData();
    await loadParcels();
    await loadAllParcels();
    await reloadUnits();
    if (areaData?.id) {
      await loadOwners();
    }
  }, [
    loadAreaData,
    loadParcels,
    loadAllParcels,
    loadOwners,
    reloadUnits,
    areaData?.id,
  ]);

  const handleAnlagePositionChange = useCallback(
    async (unitId: string, breitengrad: number, laengengrad: number) => {
      await updateUnit(unitId, { breitengrad, laengengrad });
    },
    [updateUnit],
  );

  const handleParcelClick = useCallback(
    (parcelId: string | null) => {
      setSelectedOwnerId(null);
      handleParcelClickInternal(parcelId);
    },
    [handleParcelClickInternal],
  );

  const handleOwnerClick = useCallback(
    (ownerId: string | null) => {
      if (ownerId) {
        handleParcelClickInternal(null);
      }
      setSelectedOwnerId(ownerId);
    },
    [handleParcelClickInternal],
  );

  const clearSelection = useCallback(() => {
    handleParcelClick(null);
    handleOwnerClick(null);
  }, [handleParcelClick, handleOwnerClick]);

  // Status-Filter Hook
  const {
    displayedParcels,
    displayedOwners,
    handleParcelStatusClick,
    handleOwnerStatusClick,
    clearFilter,
  } = useStatusFilter({
    projectId: project.id,
    areaId: areaData?.id,
    allParcels,
    allOwners,
    defaultParcels: parcels,
    defaultOwners: filteredOwners,
    mapSectionRef,
    onClearSelection: clearSelection,
  });

  const handleParcelUpdate = (_updatedParcel: any) => {};

  const handleOwnerStatusChange = async (ownerId: string, status: string) => {
    try {
      await updateOwnerStatus(ownerId, status);
      showSuccess("Status aktualisiert");
      await reloadUnits();

      if (
        areaData?.flaechenakquise_status === FLAECHENAKQUISE_NICHT_GESTARTET
      ) {
        setStatusPopup("gestartet");
      }
    } catch (error: any) {
      showError("Status-Update fehlgeschlagen");
    }
  };

  const handleConfirmStatusPopup = useCallback(async () => {
    if (!statusPopup) return;
    const newStatus =
      statusPopup === "gestartet"
        ? FLAECHENAKQUISE_LAEUFT
        : FLAECHENAKQUISE_ABGESCHLOSSEN;

    try {
      await apiService.updateProjectArea(project.id, {
        flaechenakquise_status: newStatus,
      });
      await loadAreaData();
      showSuccess("Status aktualisiert");
    } catch {
      showError("Status-Update fehlgeschlagen");
    } finally {
      setStatusPopup(null);
    }
  }, [statusPopup, project.id, loadAreaData, showSuccess, showError]);

  const selectionContext: SelectionContext = React.useMemo(() => {
    if (selectedOwnerId) return "owner";
    if (selectedParcelId) return "parcel";
    return null;
  }, [selectedOwnerId, selectedParcelId]);

  // Auto-Save Hook für Owner-Notizen
  useAutoSave(ownerNotizen, {
    delay: 1000,
    enabled: canEdit && isEditMode && selectedOwnerId !== null,
    onSave: async (notizen) => {
      if (!selectedOwnerId) return;
      await updateOwnerNotes(selectedOwnerId, notizen);
      showSuccess("Gespeichert");
    },
    onError: () => {
      showError("Speichern fehlgeschlagen");
    },
  });

  const relevantParcels = useMemo(
    () => allParcels.filter((p: any) => p.relevant),
    [allParcels],
  );

  const securedPercent = useMemo(() => {
    let total = 0;
    let secured = 0;
    relevantParcels.forEach((p: any) => {
      const area = p.projektFlaeche ?? p.flaeche ?? 0;
      total += area;
      if ((p.status || "Kein Kontakt") === "Vertrag abgeschlossen") {
        secured += area;
      }
    });
    return total > 0 ? (secured / total) * 100 : 0;
  }, [relevantParcels]);

  // Popup "Flächen gesichert", sobald die relevanten Flurstücke
  // (Windrad-Radius) zu 100% gesichert sind und der Status das noch nicht widerspiegelt.
  useEffect(() => {
    if (
      relevantParcels.length > 0 &&
      securedPercent >= 100 &&
      areaData?.flaechenakquise_status &&
      areaData.flaechenakquise_status !== FLAECHENAKQUISE_ABGESCHLOSSEN
    ) {
      setStatusPopup("abgeschlossen");
    }
  }, [
    securedPercent,
    relevantParcels.length,
    areaData?.flaechenakquise_status,
  ]);

  const mapTitleSuffix = (
    <StatChip
      value={`${formatNumber(securedPercent, 1)} %`}
      label="der Fläche gesichert"
      backgroundColor={colors.brand.teal50}
      valueColor={colors.brand.teal600}
    />
  );

  return (
    <PageTemplate isLoading={isLoadingArea} error={loadError}>
      <div ref={mapSectionRef}>
        <MapSection
          titleSuffix={mapTitleSuffix}
          projectId={project.id}
          projectName={project.name}
          geometrie={areaData?.geometrie}
          areaId={areaData?.id}
          parcels={displayedParcels}
          selectedParcelId={selectedParcelId || undefined}
          onParcelClick={handleParcelClick}
          onParcelUpdate={handleParcelUpdate}
          onGeometrieChanged={handleGeometrieChanged}
          anlagen={units}
          onAnlagePositionChange={handleAnlagePositionChange}
          standortKoordinate={
            areaData?.breitengrad != null && areaData?.laengengrad != null
              ? {
                  breitengrad: areaData.breitengrad,
                  laengengrad: areaData.laengengrad,
                }
              : undefined
          }
          onStandortSaved={handleKoordinateChange}
        />
      </div>

      {allParcels.length > 0 && (
        <Grid container spacing={3} sx={{ mb: 0 }}>
          <Grid size={{ xs: 12, md: 6 }}>
            <ParcelsSection
              parcels={displayedParcels}
              selectedParcelId={selectedParcelId}
              selectedParcel={selectedParcel}
              isLoadingParcels={isLoadingParcels}
              onParcelClick={handleParcelClick}
              onBack={() => {
                clearSelection();
                clearFilter();
              }}
              parcelNotizen={parcelNotizen}
              setParcelNotizen={setParcelNotizen}
              canEdit={canEdit}
              isEditMode={isEditMode}
              context={selectionContext}
            />
          </Grid>

          <Grid size={{ xs: 12, md: 6 }}>
            <OwnersSection
              owners={displayedOwners}
              isLoadingOwners={isLoadingOwners}
              context={selectionContext}
              selectedOwnerId={selectedOwnerId}
              onOwnerClick={handleOwnerClick}
              selectedOwner={selectedOwner}
              ownerNotizen={ownerNotizen}
              setOwnerNotizen={setOwnerNotizen}
              onStatusChange={handleOwnerStatusChange}
              canEdit={canEdit}
              isEditMode={isEditMode}
            />
          </Grid>
        </Grid>
      )}

      <AreaInfoSection
        flaechenbeschreibung={flaechenbeschreibung}
        setFlaechenbeschreibung={setFlaechenbeschreibung}
        isEditMode={isEditMode}
        canEdit={canEdit}
      />

      <ConfirmDialog
        open={statusPopup !== null}
        title={
          statusPopup === "gestartet"
            ? "Flächensicherung gestartet"
            : "Flächen gesichert"
        }
        message={
          statusPopup === "gestartet"
            ? 'Die Flächenakquise wurde offenbar gestartet. Soll der Status auf „Flächensicherung läuft" gesetzt werden?'
            : 'Die relevanten Flurstücke sind zu 100 % gesichert. Soll der Status auf „Flächen gesichert" gesetzt werden?'
        }
        onConfirm={handleConfirmStatusPopup}
        onCancel={() => setStatusPopup(null)}
      />
    </PageTemplate>
  );
};

export default AreaPage;
